import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';
import type { Invitation } from '../tenant/invitation';
import { PlayerAccountStatus } from './player-account-status';
import { Position } from './position';

export type PlayerId = Player['id'];

export interface PlayersByPosition {
  [position: string]: Player[];
}

export type PlayerAttributes = z.output<typeof playerAttributesSchema>;
export type PlayerAttributesDTO = z.input<typeof playerAttributesSchema>;

// Tolerant read shape: legacy data may hold values (e.g. emails) that would not
// pass today's write validation, so plain strings are accepted here.
export const playerAttributesSchema = z
  .object({
    birthday: isoDatetimeToDate,
    cv_april_tag: z.number(),
    cv_groups: z.array(z.uuid()),
    email: z.string(),
    enableNotifications: z.boolean(),
    /** The player's own opt-in to text messages. Never written by a coach. */
    enableSmsNotifications: z.boolean(),
    height: z.number(),
    number: z.string(),
    phone: z.string(),
    position: z.enum(Position),
    /** When the player consented to receive text messages at {@link smsConsentPhone}. */
    smsConsentAt: isoDatetimeToDate,
    /**
     * The number the consent was given for, in E.164.
     *
     * Kept next to `phone` rather than trusting it: `phone` is also a coach-editable attribute,
     * and consent given for one number is not consent for whatever replaces it.
     */
    smsConsentPhone: z.string(),
    /** When the number was found on the carrier opt-out list, i.e. the player replied STOP. */
    smsOptOutAt: isoDatetimeToDate,
    weight: z.number(),
  })
  .partial();

export type PlayerAttributesPayload = z.output<typeof playerAttributesPayloadSchema>;
export type PlayerAttributesPayloadDTO = z.input<typeof playerAttributesPayloadSchema>;

// Strict write shape: `null` clears an attribute, absent keys leave it untouched.
export const playerAttributesPayloadSchema = z
  .object({
    birthday: isoDatetimeToDate.nullable(),
    // cv_april_tag is intentionally omitted: write only via assignAprilTag.
    cv_groups: z.array(z.uuid()).nullable(),
    email: z.email().nullable(),
    enableNotifications: z.boolean().nullable(),
    height: z.number().nullable(),
    number: z.string().nullable(),
    phone: z.string().nullable(),
    position: z.enum(Position).nullable(),
    weight: z.number().nullable(),
  })
  .partial();

export type Player = z.output<typeof playerSchema>;
export type PlayerDTO = z.input<typeof playerSchema>;

export const playerSchema = z.object({
  attributes: playerAttributesSchema,
  attributesDate: isoDatetimeToDate,
  firstName: z.string(),
  id: z.uuid(),
  imageUrl: z.string().nullable(),
  lastName: z.string(),
  teamIds: z.array(z.uuid()),
  // Dynamically added field
  // This is not part of the player schema, but is added by the service layer
  hasAccount: z.boolean().optional(),
});

/** How the player app renders the state of a player's text message opt-in. */
export type SmsNotificationStatus = 'off' | 'active' | 'optedOut';

export const PlayerUtils = {
  getAccountStatus(player: Player, invitation: Invitation | null | undefined): PlayerAccountStatus {
    if (player.hasAccount) {
      return PlayerAccountStatus.LINKED;
    }

    if (!invitation) {
      return PlayerAccountStatus.NONE;
    }

    if (invitation.expiresAt.getTime() < Date.now()) {
      return PlayerAccountStatus.EXPIRED;
    }

    return PlayerAccountStatus.INVITED;
  },
  getFullName(player: Player): string {
    return `${player.firstName} ${player.lastName}`.trim();
  },
  /**
   * The number a player may be texted at, or `null` when they may not be texted at all.
   *
   * The single place that decides it, so the player app, the report handlers and the opt-out
   * sweep cannot disagree about who has consented. All three conditions have to hold: the opt-in
   * is on, a number is stored, and the consent on record was given for *that* number — a coach
   * editing `phone` afterwards therefore silently stops delivery instead of inheriting consent.
   *
   * @param player - The player to resolve the recipient of.
   */
  getSmsRecipient(player: Player): string | null {
    const { enableSmsNotifications, phone, smsConsentAt, smsConsentPhone } = player.attributes;

    if (enableSmsNotifications !== true || !phone || !smsConsentAt) {
      return null;
    }

    return smsConsentPhone === phone ? phone : null;
  },
  /**
   * The opt-in state to render, which is not simply the negation of
   * {@link PlayerUtils.getSmsRecipient}: a player who replied STOP is told so, rather than being
   * shown the same empty form as a player who never opted in.
   *
   * @param player - The player to resolve the status of.
   */
  getSmsStatus(player: Player): SmsNotificationStatus {
    if (PlayerUtils.getSmsRecipient(player)) {
      return 'active';
    }

    return player.attributes.smsOptOutAt ? 'optedOut' : 'off';
  },
};
